import React from "react";
import { useState, useEffect } from "react";
import {
    Modal,
    Fade,
    Box,
    Typography,
    Backdrop,
    TextField,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tabs,
    Tab,
    Divider,
} from "@mui/material";
import axios from "axios";
import Cookies from "js-cookie";
import useNotification from "../common/Notification";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "50%",
    maxHeight: "70vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    p: 4,
};

// Base URL for API calls
const base_url = "http://api.virtualprachar.com/api";
const Base_url = "http://api.virtualprachar.com/api/whatsapp/personal/group";

const DeleteGroup = ({ opening, closing, open }) => {
    const [backdropOpen, setBackdropOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState("");
    const [options, setOptions] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({
        file: null,
    });

    const [errors, setErrors] = useState({
        group: false,
        file: false,
    });

    // Notification
    const showNotification = useNotification();
    const userId = Cookies.get("userId");

    // Fetch groups on component mount
    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const res = await axios.get(`${Base_url}/get/${userId}`);
                console.log(res.data.data);

                let uniqueGroups = new Set();
                for (let obj of res.data.data) {
                    uniqueGroups.add(obj.group_name);
                }

                let groupList = [];
                uniqueGroups.forEach((group) => {
                    groupList.push(group);
                });

                setOptions(groupList);
            } catch (e) {
                console.error("Error: ", e);
                showNotification(
                    "Something went wrong. Please refresh the page.",
                    "error"
                );
            }
        };

        fetchGroups();
    }, [userId, open]);

    const handleClose = () => {
        closing();
        setSelectedGroup("");
        setFormData({ file: null });
        setErrors({ group: false, file: false });
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setSelectedGroup("");
        setFormData({ file: null });
        setErrors({ group: false, file: false });
    };

    const handleGroupChange = (e) => {
        setSelectedGroup(e.target.value);
        setErrors({ ...errors, group: false });
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFormData({ ...formData, file: selectedFile });
            setErrors({ ...errors, file: false });
        }

        // Reset input so the same file can be selected again
        e.target.value = "";
    };

    // Delete entire group
    const handleDeleteGroup = async () => {
        if (!selectedGroup) {
            setErrors({ ...errors, group: true });
            showNotification("Please select a group to delete", "error");
            return;
        }

        try {
            setBackdropOpen(true);
            const payload = {
                group_name: selectedGroup.trim(),
                user_id: userId,
            };

            const res = await axios.post(
                `${base_url}/delete/groupbyname`,
                payload
            );

            showNotification("Group deleted successfully", "success");
            // Remove the deleted group from options
            setOptions(options.filter((group) => group !== selectedGroup));
            setSelectedGroup("");
        } catch (e) {
            console.error("Error deleting group: ", e);
            showNotification(
                e.response?.data?.message || "Failed to delete group",
                "error"
            );
        } finally {
            setBackdropOpen(false);
        }
    };

    // Delete numbers from group
    const handleDeleteNumbers = async (e) => {
        e.preventDefault();

        if (!selectedGroup) {
            setErrors({ ...errors, group: true });
            showNotification("Please select a group", "error");
            return;
        }

        if (!formData.file) {
            setErrors({ ...errors, file: true });
            showNotification("Please select an Excel file", "error");
            return;
        }

        const submitData = new FormData();
        submitData.append("group_name", selectedGroup);
        submitData.append("file", formData.file);
        submitData.append("user_id", userId);

        try {
            setBackdropOpen(true);
            const res = await axios.post(
                `${base_url}/delete/numberbygroupname`,
                submitData
            );

            showNotification("Numbers deleted successfully", "success");
            setFormData({ file: null });
        } catch (e) {
            console.error("Error deleting numbers: ", e);

            if (e.response?.status === 409) {
                showNotification(`${e.response?.data?.message}`, "error");
            } else if (e.response?.status === 413) {
                showNotification(`${e.response?.data?.message}`, "error");
            } else if (e.response?.status === 422) {
                showNotification(`${e.response?.data?.message}`, "error");
            } else {
                showNotification(
                    e.response?.data?.message || "Failed to delete numbers",
                    "error"
                );
            }
        } finally {
            setBackdropOpen(false);
        }
    };

    return (
        <div>
            <Modal
                aria-labelledby="group-management-modal"
                aria-describedby="manage-your-groups"
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Typography variant="h5" component="h2" gutterBottom>
                            Group Management
                        </Typography>

                        <Box
                            sx={{
                                borderBottom: 1,
                                borderColor: "divider",
                                mb: 2,
                            }}
                        >
                            <Tabs value={activeTab} onChange={handleTabChange}>
                                <Tab label="Delete Numbers from Group" />
                                <Tab label="Delete Entire Group" />
                            </Tabs>
                        </Box>

                        {/* Group Selection */}
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel>Select Group</InputLabel>
                            <Select
                                value={selectedGroup}
                                label="Select Group"
                                onChange={handleGroupChange}
                                error={errors.group}
                            >
                                {options.map((group, index) => (
                                    <MenuItem key={index} value={group}>
                                        {group}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.group && (
                                <Typography color="error" variant="caption">
                                    Please select a group
                                </Typography>
                            )}
                        </FormControl>

                        {/* Delete Numbers Tab Content */}
                        {activeTab === 0 && (
                            <Box
                                component="form"
                                onSubmit={handleDeleteNumbers}
                            >
                                <Box sx={{ mb: 2 }}>
                                    <Button
                                        variant="contained"
                                        component="label"
                                    >
                                        Upload Excel File
                                        <input
                                            type="file"
                                            hidden
                                            accept=".xls, .xlsx"
                                            onChange={handleFileChange}
                                        />
                                    </Button>
                                </Box>

                                {formData.file && (
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        Selected file: {formData.file.name}
                                    </Typography>
                                )}

                                {errors.file && (
                                    <Typography
                                        color="error"
                                        variant="body2"
                                        sx={{ mb: 2 }}
                                    >
                                        Please select an Excel file
                                    </Typography>
                                )}

                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    disabled={backdropOpen}
                                >
                                    Delete Numbers from Group
                                    {backdropOpen && (
                                        <CircularProgress
                                            size={24}
                                            sx={{
                                                position: "absolute",
                                                top: "50%",
                                                left: "50%",
                                                marginTop: "-12px",
                                                marginLeft: "-12px",
                                            }}
                                        />
                                    )}
                                </Button>

                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        marginTop: 2,
                                        color: "text.secondary",
                                    }}
                                >
                                    <span style={{ fontWeight: "bold" }}>
                                        Note:{" "}
                                    </span>
                                    Upload an Excel file with a "phone_number"
                                    column containing the numbers you want to
                                    remove from the selected group.
                                </Typography>
                            </Box>
                        )}

                        {/* Delete Group Tab Content */}
                        {activeTab === 1 && (
                            <Box>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    Are you sure you want to delete the entire
                                    group "
                                    {selectedGroup || "[No group selected]"}"?
                                    This action cannot be undone.
                                </Typography>

                                <Button
                                    variant="contained"
                                    color="error"
                                    fullWidth
                                    onClick={handleDeleteGroup}
                                    disabled={backdropOpen || !selectedGroup}
                                >
                                    Delete Entire Group
                                    {backdropOpen && (
                                        <CircularProgress
                                            size={24}
                                            sx={{
                                                position: "absolute",
                                                top: "50%",
                                                left: "50%",
                                                marginTop: "-12px",
                                                marginLeft: "-12px",
                                            }}
                                        />
                                    )}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Fade>
            </Modal>
        </div>
    );
};

export default DeleteGroup;
